import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import type { Class, ImportSummary } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { ImportSummaryView } from '@/components/import/ImportSummaryView';
import { WeeklyGrid } from '@/components/schedule/WeeklyGrid';
import { SeatMapRenderer } from '@/components/seatmap/SeatMapRenderer';
import { supabase } from '@/lib/supabase';
import { resolveDashboardPath } from '@/lib/sessionRole';
import { isMarketingOnlyMode } from '@/lib/marketingOnly';

export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    if (isMarketingOnlyMode) return;

    const { data } = await supabase.auth.getSession();
    if (data.session) {
      throw redirect({ to: resolveDashboardPath(data.session) });
    }
  },
  component: MarketingHomePage,
});

const navItems = [
  { label: 'Walkthrough', href: '#walkthrough' },
  { label: 'Roles', href: '#roles' },
  { label: 'Recital', href: '#recital-season' },
  { label: 'Pricing', href: '#pricing' },
];

const buyerConfidenceSignals = [
  {
    title: 'Switching without a rebuild',
    proof:
      'CSV validation, family records, dancer profiles, classes, terms, rooms, and faculty setup are treated as the opening workflow.',
  },
  {
    title: 'Role-specific product views',
    proof:
      'Owners, faculty, parents, and dancers get separate operating surfaces without splitting the underlying studio data.',
  },
  {
    title: 'Recital is not an afterthought',
    proof:
      'Lineups, cast overlaps, call-time planning, ticket caps, and parent-facing show details are modeled in the product preview.',
  },
  {
    title: 'Data questions before rollout',
    proof:
      'Demo data is labeled, privacy and terms links are public, and access/export questions are reviewed before a studio launch is promised.',
  },
] as const;

const walkthroughSteps = [
  {
    id: 'setup',
    title: 'Set up your studio',
    summary:
      'Import families, dancers, classes, terms, rooms, and faculty into one clean operating base.',
    capabilities: ['CSV validation', 'Family records', 'Dancer profiles', 'Terms and rooms'],
    why: 'Moving software should not feel like rebuilding your studio from scratch.',
    screen: 'import',
  },
  {
    id: 'schedule',
    title: 'Build the schedule',
    summary:
      'Plan a weekly class calendar with instructors, rooms, capacities, age levels, and conflict visibility.',
    capabilities: ['Weekly grid', 'Room assignments', 'Capacity tracking', 'Class conflict review'],
    why: 'Everyone works from the same schedule instead of a spreadsheet that drifts.',
    screen: 'schedule',
  },
  {
    id: 'registration',
    title: 'Open registration',
    summary:
      'Invite families into a parent portal where dancer records, class choices, and waitlists stay connected.',
    capabilities: [
      'Parent registration',
      'Dancer profiles',
      'Class enrollment',
      'Waitlist visibility',
    ],
    why: 'Registration becomes the start of the season record, not a pile of forms to re-key later.',
    screen: 'registration',
  },
  {
    id: 'attendance',
    title: 'Run attendance from the floor',
    summary:
      'Faculty see only the classes they teach and mark attendance from a simple class-scoped roster.',
    capabilities: [
      'Faculty class view',
      'Tap-to-mark status',
      'Attendance history',
      'Dancer notes',
    ],
    why: 'Teachers can keep the floor moving while owners still get reliable attendance data.',
    screen: 'attendance',
  },
  {
    id: 'billing',
    title: 'Manage tuition and family billing',
    summary:
      'Handle recurring tuition, discounts, payment plans, late fees, split-family statements, and account history.',
    capabilities: [
      'Autopay status',
      'Payment plans',
      'Sibling discounts',
      'Family statements',
      'Late-fee workflows',
    ],
    why: 'Billing becomes a studio workflow, not a private notebook and a weekend reconciliation project.',
    screen: 'billing',
  },
  {
    id: 'communication',
    title: 'Communicate with families',
    summary:
      'Send announcements by class, family, dancer, or season and keep replies organized in one shared place.',
    capabilities: ['Announcements', 'Message history', 'Support requests', 'Family context'],
    why: 'Parents get clearer answers and staff stop hunting through separate message threads.',
    screen: 'communication',
  },
  {
    id: 'recital',
    title: 'Run the recital',
    summary:
      'Build show order, review overlaps, plan call times, manage caps, and surface parent-facing recital details.',
    capabilities: [
      'Drag-and-drop lineup',
      'Overlap warnings',
      'Call-time planning',
      'Seat-map caps',
    ],
    why: 'The same roster and class data powers recital planning, so you are not starting over in May.',
    screen: 'recital',
  },
  {
    id: 'competition',
    title: 'Manage choreography and competition season',
    summary:
      'Keep secure choreography media, cast-specific access, and per-dancer competition itineraries in context.',
    capabilities: [
      'Secure video access',
      'Cast-specific sharing',
      'Competition itineraries',
      'Teacher visibility',
    ],
    why: 'Families and teachers see the weekend plan without another document chain.',
    screen: 'competition',
  },
  {
    id: 'visibility',
    title: 'See the business clearly',
    summary:
      'Watch enrollment, attendance, tuition status, support requests, and recital milestones from the owner view.',
    capabilities: ['Student count', 'Class capacity', 'Attendance trends', 'At-risk items'],
    why: 'Owners can spot the next operational problem before it becomes another late-night surprise.',
    screen: 'dashboard',
  },
  {
    id: 'carry-forward',
    title: 'Carry forward into the next term',
    summary:
      'Use prior rosters, class history, family records, billing history, and schedule templates to start again.',
    capabilities: ['Roster rollover', 'Class history', 'Family records', 'Schedule templates'],
    why: 'Each season makes the next one easier instead of resetting the studio to zero.',
    screen: 'carry-forward',
  },
] as const;

const pricingTiers = [
  {
    name: 'Boutique',
    range: '0-25 students',
    price: '$39',
    fit: 'Small studios and new programs',
    includes: ['Core family and dancer records', 'Class schedule and roster basics'],
    reassurance:
      'Best when the owner wants a clean start before adding complex tuition or recital workflows.',
  },
  {
    name: 'Studio',
    range: '26-75 students',
    price: '$69',
    fit: 'Independent studios ready to leave spreadsheets',
    includes: ['Registration, rosters, attendance', 'Tuition workflow and family communication'],
    reassurance:
      'Designed for the first serious move away from spreadsheet-and-message-thread operations.',
    featured: true,
  },
  {
    name: 'Growth',
    range: '76-150 students',
    price: '$129',
    fit: 'Studios adding faculty, programs, and recital depth',
    includes: ['Faculty class views and notes', 'Recital planning and production context'],
    reassurance:
      'Fits studios where handoffs between office, teachers, and families are becoming the risk.',
  },
  {
    name: 'Scale',
    range: '151-300 students',
    price: '$229',
    fit: 'Larger studios that need tighter operating visibility',
    includes: [
      'Owner dashboard and reporting views',
      'Deeper billing, messaging, and season operations',
    ],
    reassurance:
      'Built for teams that need fewer private spreadsheets and clearer operating accountability.',
  },
  {
    name: 'Enterprise',
    range: '301+ students',
    price: 'Custom',
    fit: 'Multi-location or custom support needs',
    includes: [
      'Studio-fit review before scoping',
      'Migration, support, and workflow depth confirmed together',
    ],
    reassurance:
      'Use this when student count, process complexity, or support requirements need a real review.',
  },
] as const;

const demoClasses = [
  {
    id: 'class-ballet-3',
    name: 'Ballet III',
    weekday: 'monday',
    start_time: '16:00',
    end_time: '17:15',
    room: 'Studio A',
  },
  {
    id: 'class-tap-jr',
    name: 'Junior Tap',
    weekday: 'tuesday',
    start_time: '17:00',
    end_time: '17:45',
    room: 'Studio B',
  },
  {
    id: 'class-lyrical',
    name: 'Lyrical Company',
    weekday: 'wednesday',
    start_time: '18:00',
    end_time: '19:15',
    room: 'Studio A',
  },
  {
    id: 'class-hip-hop',
    name: 'Hip Hop 7-9',
    weekday: 'thursday',
    start_time: '16:30',
    end_time: '17:15',
    room: 'Studio C',
  },
  {
    id: 'class-pointe',
    name: 'Pre-Pointe',
    weekday: 'friday',
    start_time: '17:30',
    end_time: '18:30',
    room: 'Studio A',
  },
] as Class[];

const importSummary: ImportSummary = {
  totalRows: 184,
  validRows: 176,
  invalidRows: 2,
  warnings: [
    {
      row: 0,
      kind: 'duplicate_in_file',
      message: 'Two dancer rows share the same guardian email and last name. Review before commit.',
    },
    {
      row: 0,
      kind: 'foreign_key_missing',
      message: 'Seven classes have no room assigned yet.',
    },
  ],
  errors: [
    {
      row: 41,
      column: 'guardian_email',
      kind: 'row_invalid',
      message: 'Email address is missing an @ symbol.',
    },
    {
      row: 118,
      column: 'class_name',
      kind: 'header_missing',
      message: 'Class name is required before import.',
    },
  ],
};

const seatSections = [
  {
    id: 'center',
    name: 'Center Orchestra',
    display_order: 1,
    row_count: 3,
    seat_count_per_row: 10,
    pricing_cents: 2400,
  },
  {
    id: 'left',
    name: 'Left Wing',
    display_order: 2,
    row_count: 2,
    seat_count_per_row: 8,
    pricing_cents: 1800,
  },
];

const demoSeats = seatSections.flatMap((section) =>
  Array.from({ length: section.row_count }, (_, rowIndex) =>
    Array.from({ length: section.seat_count_per_row }, (_, seatIndex) => ({
      id: `${section.id}-${rowIndex + 1}-${seatIndex + 1}`,
      section_id: section.id,
      row_label: String.fromCharCode(65 + rowIndex),
      seat_number: seatIndex + 1,
      blocked: seatIndex === 0 && rowIndex === 0 && section.id === 'left',
    })),
  ).flat(),
);

function MarketingHomePage() {
  return (
    <main id="main" className="min-h-dvh bg-[var(--color-surface)]" data-testid="marketing-home">
      <MarketingNav />
      <HeroSection />
      <BuyerConfidenceBar />
      <ProblemSection />
      <ProductWalkthrough />
      <RoleViewTabs />
      <RecitalWorkflowShowcase />
      <MigrationFlow />
      <PricingTable />
      <TrustBar />
      <EarlyAccessCTA />
    </main>
  );
}

function MarketingNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  return (
    <header className="sticky top-0 z-[var(--z-raised)] border-b border-[var(--color-border)] bg-[var(--color-surface-overlay)] backdrop-blur">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-4 lg:px-8">
        <a
          href="#main"
          className="text-[length:var(--text-xl)] font-[var(--font-display)] text-[color:var(--color-ink)]"
          onClick={() => setMobileMenuOpen(false)}
        >
          Pirouette
        </a>
        <div className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-[length:var(--text-sm)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]"
            >
              {item.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={mobileMenuOpen ? 'Close site navigation' : 'Open site navigation'}
            aria-controls="marketing-mobile-menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] text-[color:var(--color-ink)] md:hidden"
          >
            <span aria-hidden="true" className="grid gap-1.5">
              <span className="block h-0.5 w-5 rounded bg-current" />
              <span className="block h-0.5 w-5 rounded bg-current" />
              <span className="block h-0.5 w-5 rounded bg-current" />
            </span>
          </button>
          <Link
            to="/contact"
            className="hidden min-h-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-ink)] px-4 py-2 text-[length:var(--text-sm)] font-medium text-[color:var(--color-ink-on-accent)] hover:bg-[var(--color-accent-hover)] hover:text-[color:var(--color-ink-on-accent)] sm:inline-flex"
          >
            Request early access
          </Link>
        </div>
      </nav>
      {mobileMenuOpen && (
        <div id="marketing-mobile-menu" className="border-t border-[var(--color-border)] md:hidden">
          <div className="mx-auto grid max-w-7xl gap-2 px-5 py-4">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className="min-h-11 rounded-[var(--radius-md)] px-3 py-3 font-medium text-[color:var(--color-ink)] hover:bg-[var(--color-surface-sunken)]"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 inline-flex min-h-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-ink)] px-4 py-3 font-medium text-[color:var(--color-ink-on-accent)] hover:bg-[var(--color-accent-hover)] hover:text-[color:var(--color-ink-on-accent)]"
            >
              Request early access
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroSection() {
  return (
    <section className="overflow-hidden border-b border-[var(--color-border)]">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:py-14 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-10 xl:py-12">
        <div className="flex flex-col justify-center">
          <h1 className="max-w-3xl text-[length:clamp(2.5rem,4.1vw,4.35rem)] leading-[0.98] tracking-[0]">
            Run enrollment, billing, scheduling, and parent communication from one studio workspace.
          </h1>
          <p className="mt-5 max-w-2xl text-[length:var(--text-lg)] leading-[var(--leading-relaxed)] text-[color:var(--color-ink-muted)]">
            Built for dance studios moving off spreadsheets, disconnected tools, and manual
            follow-up. Early-access studios review setup, migration, and workflow fit before relying
            on Pirouette for a season launch.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex h-12 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-6 font-medium text-[color:var(--color-ink-on-accent)] hover:bg-[var(--color-accent-hover)] hover:text-[color:var(--color-ink-on-accent)]"
            >
              Request early access
            </Link>
            <a
              href="#walkthrough"
              className="inline-flex h-12 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-6 font-medium text-[color:var(--color-ink)] hover:bg-[var(--color-surface-sunken)] hover:text-[color:var(--color-ink)]"
            >
              See the studio workflow
            </a>
          </div>
          <p className="mt-4 text-[length:var(--text-sm)] text-[color:var(--color-ink-subtle)]">
            Early access is in active development. Demo studio data shown.
          </p>
        </div>
        <ProductScreenFrame
          title="Studio command center"
          label="Actual product-style command center preview with demo studio data"
        >
          <StudioOpsDashboardPreview />
        </ProductScreenFrame>
      </div>
    </section>
  );
}

function BuyerConfidenceBar() {
  return (
    <section className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-8 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-3 md:grid-cols-2 xl:grid-cols-4">
        {buyerConfidenceSignals.map((signal) => (
          <article
            key={signal.title}
            className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-4"
          >
            <h2 className="text-[length:var(--text-base)] font-[var(--font-body)] font-semibold tracking-[0]">
              {signal.title}
            </h2>
            <p className="mt-2 text-[length:var(--text-sm)] leading-[var(--leading-relaxed)] text-[color:var(--color-ink-muted)]">
              {signal.proof}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProblemSection() {
  const pains = [
    'Rosters live in spreadsheets that only one person trusts.',
    'Parents ask questions in group texts, inboxes, and hallway conversations.',
    'Attendance gets marked on paper and entered later, if there is time.',
    'Tuition notes, discounts, payment plans, and family balances scatter across tools.',
    'Recital season turns every workaround into another late night.',
    'Competition weekends need one schedule, not five forwarded PDFs.',
  ];

  return (
    <section className="bg-[var(--color-surface-elevated)] px-5 py-[var(--space-section)] lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <h2 className="tracking-[0] text-[var(--text-4xl)]">
            A studio can survive on workarounds until the season gets loud.
          </h2>
          <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
            Pirouette is built for the owner who already knows the work: the daily schedule, the
            family questions, the billing exceptions, the faculty handoffs, and the recital details
            that cannot slip.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {pains.map((pain) => (
            <div
              key={pain}
              className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
            >
              <p className="text-[var(--color-ink-muted)]">{pain}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductWalkthrough() {
  return (
    <section id="walkthrough" className="px-5 py-[var(--space-section)] lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h2 className="tracking-[0] text-[var(--text-4xl)]">
            Walk through the operating system, from setup to next season.
          </h2>
          <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
            Each step below uses product UI patterns from the actual application or a labeled demo
            preview built from the same product model.
          </p>
        </div>

        <div className="mt-12 grid gap-8">
          {walkthroughSteps.map((step, index) => (
            <WalkthroughStep key={step.id} step={step} index={index + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WalkthroughStep({
  step,
  index,
}: {
  step: (typeof walkthroughSteps)[number];
  index: number;
}) {
  return (
    <article
      id={`walkthrough-${step.id}`}
      className="grid gap-6 rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-sm)] lg:grid-cols-[0.8fr_1.2fr] lg:p-6"
    >
      <div className="flex flex-col justify-between p-2 lg:p-4">
        <div>
          <p className="font-mono text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
            {String(index).padStart(2, '0')}
          </p>
          <h3 className="mt-3 tracking-[0] text-[var(--text-3xl)]">{step.title}</h3>
          <p className="mt-4 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)]">
            {step.summary}
          </p>
          <ul className="mt-5 grid gap-2">
            {step.capabilities.map((capability) => (
              <li key={capability} className="flex items-center gap-2 text-[var(--text-sm)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]" />
                {capability}
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-6 border-l-2 border-[var(--color-accent)] pl-4 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          {step.why}
        </p>
      </div>
      <ProductScreenFrame title={step.title} label={`${step.title} product preview with demo data`}>
        <ScreenForStep screen={step.screen} />
      </ProductScreenFrame>
    </article>
  );
}

function ScreenForStep({ screen }: { screen: (typeof walkthroughSteps)[number]['screen'] }) {
  switch (screen) {
    case 'import':
      return <ImportSummaryView summary={importSummary} errorPreviewLimit={2} />;
    case 'schedule':
      return <WeeklyGrid classes={demoClasses} conflictedClassIds={new Set(['class-lyrical'])} />;
    case 'registration':
      return <RegistrationPreview />;
    case 'attendance':
      return <AttendancePreview />;
    case 'billing':
      return <BillingPreview />;
    case 'communication':
      return <CommunicationPreview />;
    case 'recital':
      return <RecitalPreview />;
    case 'competition':
      return <CompetitionPreview />;
    case 'dashboard':
      return <StudioOpsDashboardPreview compact />;
    case 'carry-forward':
      return <CarryForwardPreview />;
  }
}

function ProductScreenFrame({
  title,
  label,
  children,
}: {
  title: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <figure
      aria-label={label}
      className="flex flex-col overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border-strong)] bg-[oklch(17%_0.01_260)] shadow-[var(--shadow-xl)]"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 text-white">
        <figcaption className="font-medium text-[var(--text-sm)]">{title}</figcaption>
        <span className="text-[var(--text-xs)] text-white/55">Demo studio data</span>
      </div>
      <div
        className="flex-1 bg-[var(--color-surface)] p-4 text-[var(--color-ink)] sm:p-5"
        style={{ flex: '1 1 auto' }}
      >
        {children}
      </div>
    </figure>
  );
}

function StudioOpsDashboardPreview({ compact = false }: { compact?: boolean }) {
  const stats = [
    ['Active dancers', '142', '+18 this term'],
    ['Open balances', '$8.4k', '12 families'],
    ['Attendance rate', '93%', 'last 30 days'],
    ['Recital tasks', '17', '5 need owner review'],
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Owner dashboard</p>
          <h3 className="mt-1 tracking-[0] text-[var(--text-2xl)]">Monday studio pulse</h3>
        </div>
        <Badge tone="info">Early access preview</Badge>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(([label, value, hint]) => (
          <div
            key={label}
            className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-4"
          >
            <p className="text-[var(--color-ink-muted)] text-[var(--text-xs)]">{label}</p>
            <p className="mt-2 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">
              {value}
            </p>
            <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>
          </div>
        ))}
      </div>
      {!compact && (
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
            <h4 className="text-[var(--text-base)]">Today across the studio</h4>
            <div className="mt-4 space-y-3">
              {[
                ['3:45 PM', 'Ballet III', 'Studio A', '18 enrolled'],
                ['5:00 PM', 'Junior Tap', 'Studio B', '2 waitlisted'],
                ['6:15 PM', 'Company rehearsal', 'Studio A', 'costume note'],
              ].map(([time, name, room, meta]) => (
                <div
                  key={name}
                  className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-3 rounded-[var(--radius-md)] bg-[var(--color-surface-elevated)] p-3 text-[var(--text-sm)]"
                >
                  <span className="font-mono text-[var(--color-ink-subtle)]">{time}</span>
                  <span className="font-medium">{name}</span>
                  <span className="text-[var(--color-ink-muted)]">
                    {room} · {meta}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
            <h4 className="text-[var(--text-base)]">Needs attention</h4>
            <ul className="mt-4 space-y-3 text-[var(--text-sm)]">
              <li>4 families need updated autopay status.</li>
              <li>Nutcracker Act II has two overlap warnings.</li>
              <li>Competition itinerary draft is ready for review.</li>
              <li>Three support requests are waiting on admin replies.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function RegistrationPreview() {
  return (
    <PreviewGrid
      title="Parent registration"
      items={[
        ['Guardian', 'Maya Rivera', 'Verified email'],
        ['Dancer', 'Sofia Rivera', 'Ballet III, Lyrical'],
        ['Waitlist', 'Hip Hop 7-9', 'Position 2'],
        ['Visibility', 'Parent portal', 'Schedule and tuition ready'],
      ]}
    />
  );
}

function AttendancePreview() {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Faculty tablet view</p>
        <h3 className="tracking-[0] text-[var(--text-xl)]">Ballet III · Today 4:00 PM</h3>
      </div>
      {['Sofia Rivera', 'Avery Chen', 'Lena Brooks', 'Mia Patel'].map((name, index) => (
        <div
          key={name}
          className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
        >
          <span className="font-medium">{name}</span>
          <div className="flex rounded-[var(--radius-md)] border border-[var(--color-border)] text-[var(--text-xs)]">
            {['Present', 'Late', 'Excused', 'Absent'].map((status) => (
              <span
                key={status}
                className={`px-3 py-1.5 ${index === 0 && status === 'Present' ? 'bg-[var(--color-accent-muted)] font-medium' : ''}`}
              >
                {status}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BillingPreview() {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Family statement</p>
        <h3 className="tracking-[0] text-[var(--text-xl)]">Rivera family billing</h3>
      </div>
      {[
        ['Monthly tuition', '$168.00', 'Recurring'],
        ['Sibling discount', '-$18.00', 'Applied'],
        ['Costume deposit', '$45.00', 'Due Feb 15'],
        ['Payment plan', '$97.50 x 2', 'Active'],
      ].map(([label, amount, meta]) => (
        <div
          key={label}
          className="grid grid-cols-[1fr_auto] gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
        >
          <span>{label}</span>
          <span className="font-medium tabular-nums">{amount}</span>
          <span className="text-[var(--color-ink-muted)] text-[var(--text-xs)]">{meta}</span>
        </div>
      ))}
    </div>
  );
}

function CommunicationPreview() {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Shared family inbox</p>
        <h3 className="tracking-[0] text-[var(--text-xl)]">Recital week updates</h3>
      </div>
      {[
        ['Admin', 'Call times are published for Saturday.'],
        ['Parent reply', 'Can Sofia arrive with the 5:40 group?'],
        ['Faculty note', 'Costume change needs one extra helper.'],
      ].map(([from, message]) => (
        <div
          key={message}
          className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
        >
          <p className="font-medium text-[var(--text-sm)]">{from}</p>
          <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">{message}</p>
        </div>
      ))}
    </div>
  );
}

function RecitalPreview() {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Show lineup builder</p>
          <h3 className="tracking-[0] text-[var(--text-xl)]">Spring Showcase · Act I</h3>
        </div>
        <Badge tone="warn">2 overlap warnings</Badge>
      </div>
      {['Opening Ballet', 'Junior Tap', 'Lyrical Company', 'Finale'].map((dance, index) => (
        <div
          key={dance}
          className="grid grid-cols-[2rem_1fr_auto] items-center gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
        >
          <span className="font-mono text-[var(--color-ink-subtle)]">{index + 1}</span>
          <span className="font-medium">{dance}</span>
          <span className="text-[var(--color-ink-muted)] text-[var(--text-xs)]">
            {index === 2 ? 'Quick-change review' : 'Cast ready'}
          </span>
        </div>
      ))}
    </div>
  );
}

function CompetitionPreview() {
  return (
    <PreviewGrid
      title="Competition weekend"
      items={[
        ['Dancer', 'Sofia Rivera', 'Solo + group routine'],
        ['Friday', '5:30 PM call', 'Room B warmup'],
        ['Media', 'Choreography video', 'Cast access only'],
        ['Admin view', 'Teacher notes', 'Visible to faculty'],
      ]}
    />
  );
}

function CarryForwardPreview() {
  return (
    <PreviewGrid
      title="Next term rollover"
      items={[
        ['Rosters', '142 records', 'Ready to roll forward'],
        ['Classes', '38 templates', 'Rooms preserved'],
        ['Families', 'Account history', 'Balances retained'],
        ['Schedule', 'Fall draft', 'Conflicts to review'],
      ]}
    />
  );
}

function PreviewGrid({ title, items }: { title: string; items: string[][] }) {
  return (
    <div className="space-y-4">
      <h3 className="tracking-[0] text-[var(--text-xl)]">{title}</h3>
      <div className="grid gap-3">
        {items.map(([label, value, detail]) => (
          <div
            key={`${label}-${value}`}
            className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
          >
            <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{label}</p>
            <p className="mt-1 font-medium">{value}</p>
            <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoleViewTabs() {
  const roles = [
    {
      id: 'owner',
      label: 'Owner/Admin',
      title: 'A command center for the whole studio',
      bullets: ['Setup', 'Scheduling', 'Tuition workflows', 'Messaging', 'Recital production'],
    },
    {
      id: 'faculty',
      label: 'Faculty',
      title: "Today's classes without the admin clutter",
      bullets: ['Class-scoped rosters', 'Attendance', 'Dancer notes', 'Production context'],
    },
    {
      id: 'parents',
      label: 'Parents',
      title: 'One place for the family schedule',
      bullets: ['Registration', 'Schedule', 'Billing', 'Announcements', 'Requests'],
    },
    {
      id: 'scale',
      label: 'Larger studios',
      title: 'Visibility for bigger operations',
      bullets: ['Locations', 'Reporting', 'Custom support', 'Season operations'],
    },
  ] as const;
  const [active, setActive] = useState<(typeof roles)[number]['id']>('owner');
  const activeRole = roles.find((role) => role.id === active) ?? roles[0];

  return (
    <section
      id="roles"
      className="bg-[var(--color-surface-elevated)] px-5 py-[var(--space-section)] lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h2 className="tracking-[0] text-[var(--text-4xl)]">
            The right view for each person in the studio.
          </h2>
          <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
            Owners need the whole operating picture. Faculty need their classes. Parents need clear
            answers. Pirouette separates those jobs without separating the data.
          </p>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-[0.7fr_1.3fr]">
          <div role="tablist" aria-label="Role views" className="grid gap-2">
            {roles.map((role) => (
              <button
                key={role.id}
                type="button"
                role="tab"
                aria-selected={role.id === active}
                onClick={() => setActive(role.id)}
                className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-left font-medium aria-[selected=true]:border-[var(--color-accent)] aria-[selected=true]:bg-[var(--color-accent-muted)]"
              >
                {role.label}
              </button>
            ))}
          </div>
          <ProductScreenFrame
            title={activeRole.label}
            label={`${activeRole.label} product role preview`}
          >
            <div className="grid gap-5 md:grid-cols-[0.9fr_1.1fr]">
              <div>
                <h3 className="tracking-[0] text-[var(--text-2xl)]">{activeRole.title}</h3>
                <ul className="mt-5 space-y-2">
                  {activeRole.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-2 text-[var(--text-sm)]">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
              <StudioOpsDashboardPreview compact />
            </div>
          </ProductScreenFrame>
        </div>
      </div>
    </section>
  );
}

function RecitalWorkflowShowcase() {
  return (
    <section
      id="recital-season"
      className="bg-[oklch(17%_0.01_260)] px-5 py-[var(--space-section)] text-white lg:px-8"
    >
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="flex flex-col justify-center">
          <h2 className="tracking-[0] text-[var(--text-4xl)] text-white">
            Recital season is where most studio software stops helping.
          </h2>
          <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--text-lg)] text-white/72">
            Pirouette makes recital part of the operating system: routines, casts, show order,
            overlap checks, call times, ticket caps, and parent-facing details work from the same
            roster and family data your studio already uses.
          </p>
          <ul className="mt-8 grid gap-3 text-white/80">
            {[
              'Drag-and-drop show lineup with conflict review.',
              'Dancer and sibling overlap visibility before the show order locks.',
              'Seat-map and per-show cap planning where configured.',
              'Secure choreography video access for the right cast.',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--color-accent-muted)]" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <ProductScreenFrame
          title="Recital operations"
          label="Recital lineup and seat map preview with demo data"
        >
          <div className="grid gap-6 xl:grid-cols-2">
            <RecitalPreview />
            <SeatMapRenderer
              sections={seatSections}
              seats={demoSeats}
              selectedSeatIds={new Set(['center-1-4', 'center-1-5', 'center-1-6'])}
              soldSeatIds={new Set(['center-2-4', 'center-2-5', 'left-1-3'])}
            />
          </div>
        </ProductScreenFrame>
      </div>
    </section>
  );
}

function MigrationFlow() {
  const steps = [
    'Import students, guardians, classes, rooms, and instructors from CSV',
    'Review warnings and errors before anything goes live',
    'Start with one workflow before moving the whole studio',
    'Confirm billing, messaging, and recital scope during setup',
    'Invite families when the operating flow is ready',
    'Keep migration questions visible during the pilot',
  ];

  return (
    <section id="migration" className="px-5 py-[var(--space-section)] lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <h2 className="tracking-[0] text-[var(--text-4xl)]">
            Switching without disrupting your season.
          </h2>
          <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
            Import once, review before launch, and move in stages. Pirouette treats migration as an
            operating workflow, not a one-time spreadsheet dump that leaves the studio guessing.
          </p>
          <div className="mt-8 grid gap-3">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-accent-muted)] font-mono text-[var(--text-xs)]">
                  {index + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
        <ProductScreenFrame title="Import validation" label="CSV import validation product preview">
          <ImportSummaryView summary={importSummary} errorPreviewLimit={2} />
        </ProductScreenFrame>
      </div>
    </section>
  );
}

function PricingTable() {
  return (
    <section
      id="pricing"
      className="bg-[var(--color-surface-elevated)] px-5 py-[var(--space-section)] lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <h2 className="tracking-[0] text-[var(--text-4xl)]">
              Simple early-access pricing by studio size.
            </h2>
            <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
              Every plan is designed around the same studio operating system. Annual billing is
              planned to save two months when launch billing opens.
            </p>
          </div>
          <p className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Which plan fits? Start with your active student count and request early access.
          </p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {pricingTiers.map((tier) => (
            <article
              key={tier.name}
              className={`rounded-[var(--radius-lg)] border bg-[var(--color-surface)] p-5 ${
                'featured' in tier && tier.featured
                  ? 'border-[var(--color-accent)] shadow-[var(--shadow-lg)]'
                  : 'border-[var(--color-border)]'
              }`}
            >
              <h3 className="tracking-[0] text-[var(--text-xl)]">{tier.name}</h3>
              <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                {tier.range}
              </p>
              <p className="mt-5 font-[var(--font-display)] text-[var(--text-3xl)]">
                {tier.price}
                {tier.price !== 'Custom' && (
                  <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]"> / mo</span>
                )}
              </p>
              <p className="mt-4 min-h-16 text-[length:var(--text-sm)] text-[color:var(--color-ink-muted)]">
                {tier.fit}
              </p>
              <div className="mt-5 border-t border-[var(--color-border)] pt-4">
                <p className="text-[length:var(--text-sm)] font-medium text-[color:var(--color-ink)]">
                  Includes
                </p>
                <ul className="mt-2 grid gap-2">
                  {tier.includes.map((item) => (
                    <li
                      key={item}
                      className="flex gap-2 text-[length:var(--text-sm)] text-[color:var(--color-ink-muted)]"
                    >
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent)]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 rounded-[var(--radius-md)] bg-[var(--color-surface-elevated)] p-3 text-[length:var(--text-xs)] leading-[var(--leading-relaxed)] text-[color:var(--color-ink-muted)]">
                  {tier.reassurance}
                </p>
              </div>
              <Link
                to="/contact"
                className="mt-5 inline-flex h-10 w-full items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] text-[length:var(--text-sm)] font-medium text-[color:var(--color-ink)] hover:bg-[var(--color-surface-sunken)] hover:text-[color:var(--color-ink)]"
              >
                Request early access
              </Link>
            </article>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-[length:var(--text-sm)] leading-[var(--leading-relaxed)] text-[color:var(--color-ink-muted)]">
          Early-access pricing is confirmed during a studio fit review. Migration scope, payment
          workflows, recital depth, and support needs are reviewed before a studio is asked to rely
          on Pirouette for a season launch.
        </p>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="px-5 py-16 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
        {[
          [
            'Built for independent studios',
            'Studio operations, recital planning, and family communication are first-class product areas.',
          ],
          [
            'Modern browser-based software',
            'No desktop install is required for the web app; native mobile releases remain separate review gates.',
          ],
          [
            'Honest early access',
            'Demo data is labeled, public legal links are available, and broad launch claims stay gated by real readiness evidence.',
          ],
        ].map(([title, copy]) => (
          <div
            key={title}
            className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-5"
          >
            <h3 className="tracking-[0] text-[var(--text-xl)]">{title}</h3>
            <p className="mt-3 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              {copy}
            </p>
          </div>
        ))}
      </div>
      <div className="mx-auto mt-8 flex max-w-7xl flex-wrap gap-4 text-[var(--text-sm)]">
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/contact">Support contact</Link>
        <Link to="/sms-consent">SMS consent</Link>
      </div>
    </section>
  );
}

function EarlyAccessCTA() {
  return (
    <section className="px-5 pb-[var(--space-section)] lg:px-8">
      <div className="mx-auto max-w-7xl rounded-[var(--radius-xl)] bg-[var(--color-ink)] p-8 text-white lg:p-12">
        <div className="max-w-3xl">
          <h2 className="tracking-[0] text-[var(--text-4xl)] text-white">
            Bring your studio into one calm operating system.
          </h2>
          <p className="mt-5 leading-[var(--leading-relaxed)] text-[var(--text-lg)] text-white/72">
            Request early access to review the product, talk through your studio size, and see which
            workflows are ready for your season.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex h-12 items-center justify-center rounded-[var(--radius-md)] bg-white px-6 font-medium text-[var(--color-ink)] hover:bg-[var(--color-accent-muted)] hover:text-[var(--color-ink)]"
            >
              Request early access
            </Link>
            <a
              href="#walkthrough"
              className="inline-flex h-12 items-center justify-center rounded-[var(--radius-md)] border border-white/25 px-6 font-medium text-white hover:bg-white/10 hover:text-white"
            >
              See the studio workflow
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
